import { useState } from 'react';
import { Menu, Plus, Settings, ExternalLink, LogOut, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useAdminNavigation } from '../navigation/useAdminNavigation';
import { CommandSearch } from '../command-palette';
import PublishBanner from '../PublishBanner';
import AdminPreferencesDialog from '../preferences/AdminPreferencesDialog';

import { getAdminShellGeometry } from './adminShellGeometry';

import PwaInstallButton from '@/components/shared/PwaInstallButton';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CDN_URL } from '@/config/cdn';
import { cn } from '@/lib/utils';

const LOGO_URL = `${CDN_URL}/images/owaza-logo.jpg`;
const PUBLIC_SITE_URL = 'https://owazamartialarts.com';

/**
 * Create dropdown - quick actions for creating new content
 */
const CreateDropdown = () => {
  const navigate = useNavigate();

  const createActions = [
    { label: 'New Program', path: '/admin/website/programs?action=new' },
    { label: 'New Page', path: '/admin/website/pages?action=new' },
    { label: 'New Quiz', path: '/admin/learning/quizzes?action=new' },
    { label: 'Upload Media', path: '/admin/website/media?action=upload' },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Create"
          className="h-8 px-2 text-stone-700 hover:text-stone-900 hover:bg-stone-200/50 dark:text-amber-100 dark:hover:text-white dark:hover:bg-white/10"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Create</span>
          <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Create New</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {createActions.map((action) => (
          <DropdownMenuItem
            key={action.path}
            onClick={() => navigate(action.path)}
            className="cursor-pointer"
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Settings button - opens the current user's personal preferences
 */
const SettingsButton = ({ onOpenPreferences }) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-stone-700 hover:text-stone-900 hover:bg-stone-200/50 dark:text-amber-100 dark:hover:text-white dark:hover:bg-white/10"
      onClick={onOpenPreferences}
      aria-label="Open personal preferences"
    >
      <Settings className="h-4 w-4" />
    </Button>
  );
};

/**
 * User avatar dropdown - shows user info, view site, logout
 */
const UserAvatarDropdown = ({ onLogout, onOpenPreferences }) => {
  const [avatarTrigger, setAvatarTrigger] = useState(null);
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const name = adminUser.name || 'Admin';
  const email = adminUser.email || '';
  const picture = adminUser.picture || '';

  // Get initials from name
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={setAvatarTrigger}
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full p-0 hover:ring-2 hover:ring-amber-500/50"
          aria-label="Admin account menu"
        >
          <Avatar className="h-8 w-8 border border-amber-600 dark:border-amber-500/50">
            <AvatarImage src={picture} alt={name} />
            <AvatarFallback className="bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-amber-100 text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{name}</p>
            {email && (
              <p className="text-xs leading-none text-muted-foreground">{email}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Mobile-only: personal preferences are hidden in the top bar < sm */}
        <DropdownMenuItem
          onClick={() => onOpenPreferences(avatarTrigger)}
          className="cursor-pointer sm:hidden"
        >
          <Settings className="h-4 w-4 mr-2" />
          Personal preferences
        </DropdownMenuItem>
        <DropdownMenuSeparator className="sm:hidden" />
        <DropdownMenuItem
          onClick={() => window.open(PUBLIC_SITE_URL, '_blank')}
          className="cursor-pointer"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View Site
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="cursor-pointer text-red-600 focus:text-red-600"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Admin Header
 * Layout: [Menu] [Logo] Owaza  |  [Search... ⌘K]  |  [+Create] [⚙] [👤]
 */
const AdminHeader = ({ onLogout }) => {
  const { railExpanded, setMobileDrawerOpen, navigationPreferences = {} } = useAdminNavigation();
  const sidebarMode = navigationPreferences.sidebar_mode || 'rail';
  const shellGeometry = getAdminShellGeometry(sidebarMode, railExpanded);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferencesTrigger, setPreferencesTrigger] = useState(null);

  const openPreferences = (trigger) => {
    setPreferencesTrigger(trigger);
    setPreferencesOpen(true);
  };

  return (
    <header className={cn(
      'fixed left-0 right-0 top-0 z-30 transition-[left] duration-200 ease-out motion-reduce:transition-none',
      shellGeometry.headerOffsetClass,
    )}>
      {/* Publish Banner - integrated above header content */}
      <PublishBanner />

      <div className="admin-header shadow-lg px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14 gap-2 sm:gap-4 admin-desktop:h-16">
          {/* LEFT: Mobile menu + Logo + Title */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-stone-700 hover:text-stone-900 hover:bg-stone-200/50 dark:text-amber-100 dark:hover:text-white dark:hover:bg-white/10 admin-desktop:hidden"
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <Link
              to="/admin"
              className="flex items-center justify-center gap-2 transition-opacity hover:opacity-80 admin-touch:min-h-[44px] admin-touch:min-w-[44px] admin-desktop:hidden"
            >
              <div className="h-8 w-8 rounded-full border-2 border-amber-500 overflow-hidden flex-shrink-0">
                <img
                  src={LOGO_URL}
                  alt="Owaza"
                  className="h-full w-full object-cover"
                />
              </div>
              <span className="hidden sm:block font-bold text-stone-800 dark:text-white">Owaza</span>
            </Link>

          </div>

          {/* CENTER: Search */}
          <div className="flex-1 flex justify-center px-2">
            <CommandSearch />
          </div>

          {/* Divider - hidden on mobile */}
          <div className="hidden sm:block w-px h-6 bg-stone-300 dark:bg-white/20" />

          {/* RIGHT: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <PwaInstallButton variant="admin" className="hidden sm:inline-flex" />
            <CreateDropdown />
            {/* Personal preferences collapse into the avatar dropdown < sm */}
            <div className="hidden sm:flex items-center gap-1 sm:gap-2">
              <SettingsButton onOpenPreferences={(event) => openPreferences(event.currentTarget)} />
            </div>
            <UserAvatarDropdown
              onLogout={onLogout}
              onOpenPreferences={openPreferences}
            />
          </div>
        </div>
      </div>
      <AdminPreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        returnFocusTarget={preferencesTrigger}
      />
    </header>
  );
};

export default AdminHeader;
